/**
 * src/lib/firebase.js
 *
 * Firebase modular JS SDK (v10) init for the web-admin rebuild.
 *
 * Config lifted verbatim from lib/firebase_options.dart (web block) /
 * public/uploads/shared/firebase-config.js — same project (staffly-e3a9c),
 * same public web API key. This is the public web config, safe to ship in
 * a client bundle (Firestore/Functions security is enforced server-side by
 * firestore.rules + the *Mutate Cloud Functions, per CLAUDE.md).
 *
 * Region: Cloud Functions target me-central2 (kStafflyFunctionsRegion
 * in lib/core/constants/regions.dart — in-Kingdom client flip per DC-EB-04,
 * see REGION_ASK2_CLIENTFLIP_PACKET_2026-07-21.md; STAGED, not yet deployed
 * as of this commit). Keep this as the single place that pins the region.
 */

import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
// NOTE: `firebase/storage` is deliberately NOT imported here — see
// getStorageLazy() below. Auth, Firestore and Functions are needed by the first
// screen that renders; Storage is needed by one screen that uploads a file.

/**
 * Single cutover point for the Cloud Storage bucket — mirrors
 * lib/core/constants/storage.dart and functions/src/constants/storage.ts.
 *
 * RESIDENCY (PDPL / PLAN.md gate G1):
 *   'staffly-e3a9c.firebasestorage.app' — me-central1 (Doha, QATAR, out of
 *     Kingdom). Live today.
 *   'staffly-e3a9c-storage-mc2'         — me-central2 (in-Kingdom). Created,
 *     populated (byte-exact, download tokens preserved) and Firebase-linked
 *     2026-08-05; awaiting cutover.
 *
 * THE FLIP IS DELIBERATELY NOT ARMED. Cutover = change this one line plus the
 * matching lines in the two files above. Order: Cloud Functions first, then
 * clients — never the client ahead of the backend.
 */
export const STORAGE_BUCKET = 'staffly-e3a9c.firebasestorage.app';

export const firebaseConfig = {
  apiKey: 'AIzaSyCcU76zUBbCmjC5PTrVX5vpOz0uakFWVfg',
  authDomain: 'staffly-e3a9c.firebaseapp.com',
  projectId: 'staffly-e3a9c',
  storageBucket: STORAGE_BUCKET,
  messagingSenderId: '503016685338',
  appId: '1:503016685338:web:2e171dbd1fda1dd06db6a3',
};

/** Single cutover point for the Cloud Functions region — mirrors regions.dart. */
export const FUNCTIONS_REGION = 'me-central2';

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, FUNCTIONS_REGION);
/**
 * Cloud Storage, constructed on FIRST USE rather than at boot.
 *
 * `export const storage = getStorage(app)` put the Storage SDK into the
 * boot-path Firebase chunk (495 KB raw / 115 KB gzipped) for every visitor,
 * although exactly ONE screen uploads a file (screens/documents.js). Everyone
 * else paid for it on first paint and never touched it.
 *
 * Async, so the SDK arrives as its own chunk at the moment a user actually
 * picks a file — by which point they have already spent longer choosing it than
 * the fetch takes. The emulator wiring moved in here too: it must run when the
 * instance is CREATED, not at module scope, or an offline-demo build would talk
 * to production storage. Memoised, so repeated uploads construct once.
 *
 * @returns {Promise<import('firebase/storage').FirebaseStorage>}
 */
let _storage = null;
let _storageInflight = null;
export function getStorageLazy() {
  if (_storage) return Promise.resolve(_storage);
  if (_storageInflight) return _storageInflight;
  _storageInflight = import('firebase/storage').then(({ getStorage, connectStorageEmulator }) => {
    const s = getStorage(app);
    if (OFFLINE_DEMO) {
      const host = import.meta.env?.VITE_STAFFLY_EMULATOR_HOST || '127.0.0.1';
      connectStorageEmulator(s, host, 9199);
    }
    _storage = s;
    _storageInflight = null;
    return s;
  });
  return _storageInflight;
}

/**
 * ─── OFFLINE DEMO MODE (2026-08-08) ────────────────────────────────────────
 *
 * Points this bundle at LOCAL emulators instead of production. Built for the
 * investor walkthrough after the reseller suspended GCP billing and every
 * deployed Cloud Function stopped booting — the live demo serves nothing, and a
 * demo that depends on someone else's billing dispute is not a demo.
 *
 * OFF UNLESS EXPLICITLY ASKED FOR. It is opt-in at BUILD time via
 * VITE_STAFFLY_EMULATOR, which Vite inlines at compile time — so a normal
 * production build cannot carry this on by accident: the flag is absent, the
 * branch is statically false, and bundlers drop it.
 *
 * Run it with: npm run demo:offline (see scripts/demo/README.md).
 *
 * Deliberately NOT wired to hostname sniffing (e.g. "if localhost"), which would
 * silently change behaviour for any developer running a normal local build
 * against production data — the failure mode being avoided is a demo build that
 * looks live, or a live build that quietly talks to an empty emulator.
 */
const EMU = import.meta.env?.VITE_STAFFLY_EMULATOR;
export const OFFLINE_DEMO = EMU === '1' || EMU === 'true';

if (OFFLINE_DEMO) {
  const host = import.meta.env?.VITE_STAFFLY_EMULATOR_HOST || '127.0.0.1';
  connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, host, 8080);
  connectFunctionsEmulator(functions, host, 5001);
  // Storage's emulator wiring lives inside getStorageLazy() — the instance does
  // not exist yet at this point, and connecting it here would force the SDK
  // back into the boot chunk, undoing the split.
  // Loud on purpose: anyone looking at the console must be able to tell in one
  // glance that this build is NOT talking to production.
  // eslint-disable-next-line no-console
  console.warn(
    `%c OFFLINE DEMO — emulators at ${host} · NOT production data `,
    'background:#b45309;color:#fff;font-weight:bold;padding:2px 6px;border-radius:3px',
  );
}
